import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCollectionById,
  updateActuacion,
  createActuacion,
  deleteActuacion,
  getAllTiposReforma,
  getAllUnidades
} from '../utils/actuacionesReformaApi';

// You may want to add updateCollection to actuacionesReformaApi.js if not present
import { authFetch } from '../utils/authFetch';
function updateCollection(id, data) {
  return authFetch(`${window.REACT_APP_API_ENDPOINT}/api/actuaciones-reforma/colecciones/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

const ColeccionEditarPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ nombre: '', descripcion: '', actuaciones: [] });
  const [submitting, setSubmitting] = useState(false);
  const [tiposReforma, setTiposReforma] = useState([]);
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    setLoading(true);
    getCollectionById(id)
      .then((col) => {
        setForm({
          nombre: col.nombre,
          descripcion: col.descripcion || '',
          actuaciones: col.actuaciones || [],
        });
      })
      .catch(setError)
      .finally(() => setLoading(false));
    getAllTiposReforma().then(setTiposReforma);
    getAllUnidades().then(setUnidades);
  }, [id]);

  const handleActuacionChange = (idx, field, value) => {
    setForm((prev) => {
      const actuaciones = [...prev.actuaciones];
      actuaciones[idx] = { ...actuaciones[idx], [field]: value };
      return { ...prev, actuaciones };
    });
  };

  const handleAddActuacion = () => {
    setForm((prev) => ({
      ...prev,
      actuaciones: [
        ...prev.actuaciones,
        { nombre: '', descripcion: '', coste_material: '', mano_obra_horas: '', tipo_reforma_id: '', unidad: '' },
      ],
    }));
  };

  const handleRemoveActuacion = (idx) => {
    const act = form.actuaciones[idx];
    if (act.id) {
      // Mark for deletion
      setForm((prev) => ({
        ...prev,
        actuaciones: prev.actuaciones.map((a, i) => i === idx ? { ...a, _delete: true } : a),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        actuaciones: prev.actuaciones.filter((_, i) => i !== idx),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // 1. Update the collection
      await updateCollection(id, { nombre: form.nombre, descripcion: form.descripcion });
      // 2. Update, create, or delete actuaciones
      for (const act of form.actuaciones) {
        if (act._delete && act.id) {
          await deleteActuacion(act.id);
        } else if (act.id) {
          await updateActuacion(act.id, act);
        } else if (!act._delete) {
          await createActuacion({ ...act, coleccion_id: id });
        }
      }
      navigate('/actuaciones');
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-6">Cargando...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error.message || error.toString()}</div>;

  return (
    <div className="container mx-auto p-6 flex justify-center max-w-6xl">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-6xl">
        <h2 className="text-3xl font-bold mb-6 text-blue-800 flex items-center gap-2">
          <span>Editar modelo de costes de reforma</span>
          <span className="text-2xl">✏️</span>
        </h2>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <label className="block font-semibold mb-2 text-lg text-gray-700">Nombre de la colección</label>
            <input
              className="border rounded-lg px-4 py-2 w-full text-lg focus:ring-2 focus:ring-blue-200"
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className="block font-semibold mb-2 text-lg text-gray-700">Descripción</label>
            <textarea
              className="border rounded-lg px-4 py-2 w-full text-lg focus:ring-2 focus:ring-blue-200"
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
            />
          </div>
          <div>
            <label className="block font-semibold mb-3 text-lg text-gray-700">Actuaciones</label>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-50">
                  <tr>
                    <th className="px-4 py-3 w-56">Nombre</th>
                    <th className="px-4 py-3 w-72">Descripción</th>
                    <th className="px-4 py-3">Coste Material</th>
                    <th className="px-4 py-3">Horas Mano Obra</th>
                    <th className="px-4 py-3">Tipo Reforma</th>
                    <th className="px-4 py-3">Unidad</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.actuaciones.filter(act => !act._delete).map((act, idx) => (
                    <tr key={act.id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td>
                        <input
                          className="border rounded px-2 py-1 w-56 text-base"
                          value={act.nombre}
                          onChange={e => handleActuacionChange(idx, 'nombre', e.target.value)}
                          required
                        />
                      </td>
                      <td>
                        <input
                          className="border rounded px-2 py-1 w-72 text-base"
                          value={act.descripcion}
                          onChange={e => handleActuacionChange(idx, 'descripcion', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="border rounded px-2 py-1 w-24 text-base"
                          type="number"
                          value={act.coste_material}
                          onChange={e => handleActuacionChange(idx, 'coste_material', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          className="border rounded px-2 py-1 w-24 text-base"
                          type="number"
                          value={act.mano_obra_horas}
                          onChange={e => handleActuacionChange(idx, 'mano_obra_horas', e.target.value)}
                        />
                      </td>
                      <td>
                        <select
                          className="border rounded px-2 py-1 w-32 text-base"
                          value={act.tipo_reforma_id}
                          onChange={e => handleActuacionChange(idx, 'tipo_reforma_id', e.target.value)}
                          required
                        >
                          <option value="">Selecciona...</option>
                          {tiposReforma.map(tr => (
                            <option key={tr.id} value={tr.id}>{tr.nombre}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="border rounded px-2 py-1 w-32 text-base"
                          value={act.unidad}
                          onChange={e => handleActuacionChange(idx, 'unidad', e.target.value)}
                          required
                        >
                          <option value="">Selecciona...</option>
                          {unidades.map(u => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="text-red-600 hover:underline font-semibold"
                          onClick={() => handleRemoveActuacion(idx)}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-semibold transition"
              onClick={handleAddActuacion}
            >
              + Añadir actuación
            </button>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-green-600 text-white px-8 py-3 rounded-xl hover:bg-green-700 font-bold text-lg shadow"
              disabled={submitting}
            >
              Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ColeccionEditarPage; 